import React, { useEffect, useState } from "react";
import { UseMutateAsyncFunction } from "@tanstack/react-query";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import OptionPicker from "components/OptionPicker";
import { FirstApiError } from "hooks/api/errors";
import { useRacehorse360Api } from "hooks/api";
import {
  EMPTY_OPTION,
  IOptionPickerProps,
  transformDataToIOption
} from "../OptionPicker/OptionPicker";

type BarnNumberPickerBaseProps = Omit<
  IOptionPickerProps,
  "options" | "optionId" | "onConfirm"
>;

interface IProps extends BarnNumberPickerBaseProps {
  options: Pick<racehorse360.IBarn, "id" | "name">[];
  onBarnChange?: (patch: racehorse360.IHorse, horseId?: string) => void;
}

const BarnNumberPicker = (props: IProps) => {
  const { horse, options = [], onBarnChange, ...restProps } = props;

  const [barnId, setBarnId] = useState(horse.barn?.id || EMPTY_OPTION.id);

  const { useAssignHorseToBarn, useUnassignHorseFromBarn } =
    useRacehorse360Api();

  const { mutateAsync: assignHorseToBarn, isPending: isHorseAssigning } =
    useAssignHorseToBarn();
  const { mutateAsync: unAssignHorseFromBarn, isPending: isHorseUnAssigning } =
    useUnassignHorseFromBarn();

  const isLoading = [isHorseAssigning, isHorseUnAssigning].some(Boolean);

  const handleConfirmValue = (selectValue: string) => {
    const query = {
      horseId: horse?.id,
      barnId: null,
      getOptions: {
        select: [
          "barn.id",
          "barn.name",
          "barn.facility.name",
          "barn.facility.code",
          "stallNumber"
        ]
      }
    };

    let changeBarnRequest: UseMutateAsyncFunction<
      Partial<Omit<racehorse360.IHorse, "toJSON">>,
      FirstApiError,
      | racehorse360.IUnassignHorseFromBarnRequest
      | racehorse360.IAssignHorseToBarnRequest
    >;

    if (selectValue === EMPTY_OPTION.id) {
      changeBarnRequest = unAssignHorseFromBarn;
    } else {
      changeBarnRequest = assignHorseToBarn;
      query.barnId = selectValue;
    }

    return changeBarnRequest(query).then(horse => {
      setBarnId(horse.barn?.id || EMPTY_OPTION.id);
      onBarnChange?.(
        {
          barn: horse.barn,
          stallNumber: horse.stallNumber
        },
        horse.id
      );
    });
  };

  useEffect(() => {
    setBarnId(horse.barn?.id || EMPTY_OPTION.id);
  }, [horse.barn]);

  return (
    <OptionPicker
      options={transformDataToIOption(options)}
      optionId={barnId}
      horse={horse}
      isLoading={isLoading}
      onConfirm={handleConfirmValue}
      {...restProps}
    />
  );
};

export default React.memo(BarnNumberPicker);
